import { SyntheticEvent, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { displayAlertBoxSagaAction } from 'commons/src/actions';
import { analyticsLogger } from 'commons/src/analytics';
import { CUSTOM_THRESHOLD_SAVED } from 'commons/src/analytics/AnalyticsEvents';
import { sensorUnits } from 'commons/src/constants';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { ErrorType, ThresholdRange } from 'commons/src/models/commonTypeScript';
import {
    CustomOptions,
    ThresholdOption,
    ThresholdOptions,
    ThresholdUpdatePayload,
} from 'commons/src/models/customThresholdModels';
import { toErrorType, toErrorTypeWithMessage } from 'commons/src/sagas/isErrorType';
import { ThresholdConfigSensor, UpdateThresholdsRequest, useUpdateThresholdsMutation } from '../../../api';

export type Props = {
    sensorType: SensorTypes;
    thresholds: {
        customOptions?: CustomOptions;
        ranges: ThresholdRange[];
        unit: keyof typeof sensorUnits;
        thresholdOptions: ThresholdOptions;
    };
    thresholdScope: ThresholdsResourceScope;
    id?: string;
    updateSuccessAction?: (id: string, payload: ThresholdUpdatePayload) => void;
    parentUrl?: string;
};

export type ReturnType = {
    changeScopeSelection: (e: SyntheticEvent<HTMLInputElement>) => void;
    goToParentThresholds: () => void;
    thresholdOptions: ThresholdOption[];
    selectedScope: ThresholdsResourceScope;
    selectedValues: number[];
    handleSetSelectedValues: (values: number[]) => void;
    handleOnSave: (sensorValue: ThresholdConfigSensor) => Promise<void>;
    isLoading: boolean;
    isSaved: boolean;
    error?: ErrorType;
};
const useSensorThresholdMgmt = ({
    thresholds,
    thresholdScope,
    sensorType,
    updateSuccessAction,
    id,
    parentUrl,
}: Props): ReturnType => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [updateThresholds, { isLoading }] = useUpdateThresholdsMutation();

    const [error, setError] = useState<ErrorType | undefined>(undefined);
    const [isSaved, setIsSaved] = useState(false);
    const [selectedValues, setSelectedValues] = useState<number[]>([]);
    const [selectedScope, setSelectedThresholdScope] = useState(
        thresholds.thresholdOptions.activeScope || thresholdScope
    );
    const changeScopeSelection = (e: SyntheticEvent<HTMLInputElement>): void => {
        const { value } = e.currentTarget;
        setSelectedThresholdScope(value as ThresholdsResourceScope);
    };
    const goToParentThresholds = (): void => {
        if (parentUrl) navigate(parentUrl);
    };

    const thresholdOptions = useMemo(() => {
        const { options } = thresholds.thresholdOptions;
        return (selectedScope && options?.[selectedScope]) || [];
    }, [selectedScope, thresholds.thresholdOptions.options]);

    const logThresholdSaved = (request: UpdateThresholdsRequest): void => {
        analyticsLogger(CUSTOM_THRESHOLD_SAVED, {
            resourceScope: request.resourceScope,
            id: request.id,
            sensors: request.sensors.map(sensor => ({
                sensor: sensor.sensor,
                thresholds: sensor.thresholds.map(threshold => ({
                    thresholdType: threshold.thresholdType,
                    value: threshold.value,
                })),
            })),
        });
    };

    const handleOnSave = async (sensorValue: ThresholdConfigSensor): Promise<void> => {
        if (!id) return;
        setError(undefined);
        const updatedThresholds = selectedScope === thresholdScope ? sensorValue.thresholds : [];
        const request = {
            resourceScope: thresholdScope,
            id,
            sensors: [{ sensor: sensorValue.sensor, thresholds: updatedThresholds }],
        };
        logThresholdSaved(request);
        try {
            await updateThresholds(request).unwrap();
            setIsSaved(true);
            if (thresholdScope !== ThresholdsResourceScope.SPACE) {
                dispatch(displayAlertBoxSagaAction('CustomThresholds.UpdateSuccess', false, true));
            }
            if (updateSuccessAction) {
                updateSuccessAction(id, {
                    [sensorType]: {
                        thresholds: sensorValue.thresholds.map(t => ({
                            type: t.thresholdType,
                            value: t.value,
                        })),
                    },
                });
            }
        } catch (err) {
            if (thresholdScope === ThresholdsResourceScope.SPACE) {
                setError(toErrorType(err));
            }
            dispatch(displayAlertBoxSagaAction(toErrorTypeWithMessage(err).message, true, true));
        }
    };
    const handleSetSelectedValues = (values: number[]): void => {
        setSelectedValues(values);
        if (isSaved) setIsSaved(false);
    };
    return {
        changeScopeSelection,
        goToParentThresholds,
        thresholdOptions,
        selectedScope,
        selectedValues,
        handleSetSelectedValues,
        handleOnSave,
        isLoading,
        isSaved,
        error,
    };
};

export default useSensorThresholdMgmt;
